/**
 * Utilitaire pour compresser les images avant upload
 */

/**
 * Compresse une image en réduisant sa qualité et/ou sa taille
 * @param {File} file - Le fichier image à compresser
 * @param {Object} options - Options de compression
 * @param {number} options.maxWidth - Largeur maximale (défaut: 1024)
 * @param {number} options.maxHeight - Hauteur maximale (défaut: 1024)
 * @param {number} options.quality - Qualité de compression 0-1 (défaut: 0.8)
 * @param {number} options.maxSizeMB - Taille maximale en MB (défaut: 2)
 * @returns {Promise<File>} - Le fichier compressé
 */
export const compressImage = (file, options = {}) => {
  return new Promise((resolve, reject) => {
    const {
      maxWidth = 1024,
      maxHeight = 1024,
      quality = 0.8,
      maxSizeMB = 2
    } = options;

    // Vérifier si c'est une image
    if (!file.type.startsWith('image/')) {
      reject(new Error('Le fichier doit être une image'));
      return;
    }

    // Si l'image est déjà petite, la retourner telle quelle
    if (file.size <= maxSizeMB * 1024 * 1024) {
      resolve(file);
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculer les nouvelles dimensions en conservant le ratio
      let { width, height } = img;
      
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width *= ratio;
        height *= ratio;
      }

      // Configurer le canvas
      canvas.width = width;
      canvas.height = height;

      // Dessiner l'image redimensionnée
      ctx.drawImage(img, 0, 0, width, height);

      // Convertir en blob avec compression
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Erreur lors de la compression'));
            return;
          }

          // Créer un nouveau fichier avec le même nom
          const compressedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now()
          });

          console.log(`Compression réussie: ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
          resolve(compressedFile);
        },
        file.type,
        quality
      );
    };

    img.onerror = () => {
      reject(new Error('Erreur lors du chargement de l\'image'));
    };

    // Charger l'image
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target.result;
    };
    reader.onerror = () => {
      reject(new Error('Erreur lors de la lecture du fichier'));
    };
    reader.readAsDataURL(file);
  });
};

/**
 * Compresse automatiquement une image selon sa taille
 * @param {File} file - Le fichier image
 * @returns {Promise<File>} - Le fichier compressé ou original
 */
export const autoCompressImage = async (file) => {
  const sizeMB = file.size / 1024 / 1024;

  // Pas de compression nécessaire si < 1MB
  if (sizeMB <= 1) {
    return file;
  }

  // Compression légère pour 1-3MB
  if (sizeMB <= 3) {
    return compressImage(file, {
      maxWidth: 1200,
      maxHeight: 1200,
      quality: 0.85,
      maxSizeMB: 1
    });
  }

  // Compression moyenne pour 3-8MB
  if (sizeMB <= 8) {
    return compressImage(file, {
      maxWidth: 1024,
      maxHeight: 1024,
      quality: 0.75,
      maxSizeMB: 1
    });
  }

  // Compression forte pour > 8MB
  return compressImage(file, {
    maxWidth: 800,
    maxHeight: 800,
    quality: 0.6,
    maxSizeMB: 1
  });
};

/**
 * Valide et prépare une image pour l'upload
 * @param {File} file - Le fichier image
 * @returns {Promise<{file: File, isCompressed: boolean, originalSize: number, finalSize: number}>}
 */
export const prepareImageForUpload = async (file) => {
  try {
    const originalSize = file.size;
    const compressedFile = await autoCompressImage(file);
    const isCompressed = compressedFile.size < originalSize;

    return {
      file: compressedFile,
      isCompressed,
      originalSize,
      finalSize: compressedFile.size
    };
  } catch (error) {
    throw new Error(`Erreur lors de la préparation de l'image: ${error.message}`);
  }
}; 